import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  Table,
  Stack,
  Button,
  TableBody,
  Container,
  IconButton,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  InputAdornment,
  Alert,
  AlertTitle,
  TablePagination,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSnackbar } from "notistack";
import { PATH_DASHBOARD } from "../../../routes/paths";
import {
  IBankTransaction,
  ITransactionCategory,
  IFinanceStats,
  IVatSummary,
} from "../../../@types/finance";
import { dateToQuarter } from "../../../utils/vatQuarter";
import { selectableTransactionCategories } from "../../../utils/financeCategories";
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import CustomBreadcrumbs from "../../../components/custom-breadcrumbs";
import { useSettingsContext } from "../../../components/settings";
import { TableNoData } from "../../../components/table";

import { FinanceService } from "../../../api/finance.service";
import FinanceTableRow from "./FinanceTableRow";
import FinanceDashboard from "./FinanceDashboard";
import ReconciliationPanel from "./ReconciliationPanel";
import VatSummaryPanel from "./VatSummaryPanel";
import ExpensesPanel from "../../../components/finance/AddExpenseDialog";
import RulesEnginePanel from "./RulesEnginePanel";
import ExportsReportsPanel from "../../../components/finance/ExportReportDialog";
import AssignCategoryDialog from "../../../components/finance/AssignCategoryDialog";
import ImportStatementDialog from "../../../components/finance/ImportStatementDialog";
import AddRuleDialog from "../../../components/finance/AddRuleDialog";
import FinancePeriodSelector, {
  SelectedPeriod,
} from "./FinancePeriodSelector";

// ── Types ─────────────────────────────────────────────────────────────────────
interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

function unwrapPaginated<T>(body: unknown): PaginatedResponse<T> {
  if (
    body &&
    typeof body === "object" &&
    !Array.isArray(body) &&
    Array.isArray((body as any).data)
  ) {
    return body as PaginatedResponse<T>;
  }
  if (Array.isArray(body)) {
    return {
      data: body as T[],
      total: (body as T[]).length,
      page: 1,
      limit: (body as T[]).length,
    };
  }
  return { data: [], total: 0, page: 1, limit: 50 };
}

// ── Constants ─────────────────────────────────────────────────────────────────
const TABLE_HEAD = [
  { id: "transactionDate", label: "Date", align: "left" as const },
  { id: "narrative", label: "Narrative", align: "left" as const },
  { id: "transactionType", label: "Type", align: "center" as const },
  { id: "amount", label: "Amount", align: "right" as const },
  { id: "category", label: "Category", align: "left" as const },
  { id: "method", label: "Method", align: "center" as const },
  { id: "vatCode", label: "VAT", align: "center" as const },
  { id: "status", label: "Status", align: "center" as const },
  { id: "actions", label: "Actions", align: "center" as const },
];

const FINANCE_TABS = [
  { value: "dashboard", label: "Dashboard", icon: "eva:grid-fill" },
  {
    value: "transactions",
    label: "Bank Transactions",
    icon: "eva:credit-card-fill",
  },
  {
    value: "unclassified",
    label: "Unclassified",
    icon: "eva:alert-triangle-fill",
  },
  {
    value: "reconciliation",
    label: "Reconciliation",
    icon: "eva:checkmark-circle-2-fill",
  },
  {
    value: "vat",
    label: "VAT & Partial Exemption",
    icon: "eva:file-text-fill",
  },
  { value: "expenses", label: "Expenses", icon: "eva:trending-down-fill" },
  { value: "rules", label: "Rules Engine", icon: "eva:settings-2-fill" },
  { value: "exports", label: "Exports & Reports", icon: "eva:download-fill" },
];

const DIRECTION_OPTIONS = [
  { label: "All Directions", value: "all" },
  { label: "Credit (in)", value: "credit" },
  { label: "Debit (out)", value: "debit" },
];

const STATUS_OPTIONS = [
  { label: "All Status", value: "all" },
  { label: "Reconciled", value: "reconciled" },
  { label: "Unmatched", value: "unmatched" },
  { label: "Pending", value: "pending" },
];

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// ── Period type label colours ─────────────────────────────────────────────────
const PERIOD_COLOR: Record<
  SelectedPeriod["periodType"],
  "primary" | "secondary" | "success"
> = {
  year: "primary",
  quarter: "secondary",
  month: "success",
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function FinancePage() {
  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();

  // ── Period selection state ─────────────────────────────────────────────────
  const [selectedPeriod, setSelectedPeriod] = useState<SelectedPeriod | null>(
    null,
  );

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Data
  const [transactions, setTransactions] = useState<IBankTransaction[]>([]);
  const [unclassified, setUnclassified] = useState<IBankTransaction[]>([]);
  const [categories, setCategories] = useState<ITransactionCategory[]>([]);
  const [financeStats, setFinanceStats] = useState<IFinanceStats | null>(null);
  const [vatSummary, setVatSummary] = useState<IVatSummary | null>(null);
  const [unclassifiedCount, setUnclassifiedCount] = useState(0);

  // UI
  const [activeTab, setActiveTab] = useState("dashboard");
  const [filterName, setFilterName] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterDirection, setFilterDirection] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMonth, setFilterMonth] = useState<Date | null>(null);

  // Dialogs
  const [assignOpen, setAssignOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [addRuleOpen, setAddRuleOpen] = useState(false);
  const [rulesRefreshKey, setRulesRefreshKey] = useState(0);
  const [periodsRefreshKey, setPeriodsRefreshKey] = useState(0);
  const [selectedTx, setSelectedTx] = useState<IBankTransaction | null>(null);

  // Loading
  const [loadingTx, setLoadingTx] = useState(false);
  const [loadingUnc, setLoadingUnc] = useState(false);
  const [connectingFeed, setConnectingFeed] = useState(false);

  const isFiltered =
    filterName !== "" ||
    filterCategory !== "all" ||
    filterDirection !== "all" ||
    filterStatus !== "all" ||
    filterMonth !== null;

  // ── When period changes, reset dashboard state ────────────────────────────
  useEffect(() => {
    if (selectedPeriod) {
      setActiveTab("dashboard");
      setPage(0);
      setFilterName("");
      setFilterCategory("all");
      setFilterDirection("all");
      setFilterStatus("all");
      setFilterMonth(null);
      setFinanceStats(null);
      setTransactions([]);
    }
  }, [selectedPeriod]);

  // ── Fetch transactions (paginated, filtered) ─────────────────────────────
  const fetchTransactions = useCallback(
    async (currentPage = page, currentLimit = rowsPerPage) => {
      try {
        setLoadingTx(true);
        const params: Record<string, any> = {
          page: currentPage + 1,
          limit: currentLimit,
        };

        // Period date range takes priority over manual month filter
        if (selectedPeriod) {
          params.dateFrom = selectedPeriod.dateFrom;
          params.dateTo = selectedPeriod.dateTo;
        } else if (filterMonth) {
          const y = filterMonth.getFullYear();
          const m = String(filterMonth.getMonth() + 1).padStart(2, "0");
          params.month = `${y}-${m}`;
        }

        if (filterName) params.search = filterName;
        if (filterCategory !== "all") params.categoryId = filterCategory;
        if (filterDirection !== "all") params.direction = filterDirection;
        if (filterStatus !== "all") params.status = filterStatus;

        const res = await new FinanceService().getTransactions(params);
        const { data: rows, total } = unwrapPaginated<IBankTransaction>(
          res.data,
        );
        setTransactions(rows);
        setTotalCount(total);
      } catch {
        enqueueSnackbar("Failed to load transactions", { variant: "error" });
        setTransactions([]);
        setTotalCount(0);
      } finally {
        setLoadingTx(false);
      }
    },
    [
      page,
      rowsPerPage,
      filterName,
      filterCategory,
      filterDirection,
      filterStatus,
      filterMonth,
      selectedPeriod,
    ],
  );

  // ── Fetch unclassified ───────────────────────────────────────────────────
  const fetchUnclassified = useCallback(async () => {
    try {
      setLoadingUnc(true);
      const params: Record<string, any> = { status: "unclassified", limit: 200, page: 1 };
      if (selectedPeriod) {
        params.dateFrom = selectedPeriod.dateFrom;
        params.dateTo = selectedPeriod.dateTo;
      }
      const res = await new FinanceService().getTransactions(params);
      const { data: rows, total } = unwrapPaginated<IBankTransaction>(res.data);
      setUnclassified(rows);
      setUnclassifiedCount(total);
    } catch {
      setUnclassified([]);
      setUnclassifiedCount(0);
    } finally {
      setLoadingUnc(false);
    }
  }, [selectedPeriod]);

  // ── Fetch stats ──────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const params = selectedPeriod
        ? { dateFrom: selectedPeriod.dateFrom, dateTo: selectedPeriod.dateTo }
        : undefined;
      const res = await new FinanceService().getFinanceStats(params);
      setFinanceStats(res.data);
    } catch {
      console.error("Failed to fetch stats");
    }
  }, [selectedPeriod]);

  // ── Fetch VAT summary (same quarter mapping as the VAT & PE tab, so the
  // dashboard snapshot always agrees with it for the same period) ───────────
  const fetchVatSummary = useCallback(async () => {
    if (!selectedPeriod) return;
    try {
      const quarter = dateToQuarter(selectedPeriod.dateFrom);
      const res = await new FinanceService().getVatSummary(quarter);
      setVatSummary(res.data);
    } catch {
      console.error("Failed to fetch VAT summary");
    }
  }, [selectedPeriod]);

  // ── Fetch categories ─────────────────────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    try {
      const res = await new FinanceService().getCategories();
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch {
      console.error("Failed to fetch categories");
    }
  }, []);

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchStats();
      fetchVatSummary();
      fetchUnclassified();
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod && activeTab === "transactions") {
      fetchTransactions(page, rowsPerPage);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (selectedPeriod && activeTab === "transactions") {
      setPage(0);
      fetchTransactions(0, rowsPerPage);
    }
  }, [filterName, filterCategory, filterDirection, filterStatus, filterMonth]);

  useEffect(() => {
    if (!selectedPeriod) return;
    if (activeTab === "transactions") fetchTransactions(0, rowsPerPage);
    if (activeTab === "unclassified") fetchUnclassified();
    if (activeTab === "dashboard") {
      fetchStats();
      fetchVatSummary();
    }
  }, [activeTab]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleAssignSave = async (
    txId: string,
    catId: string,
    reason: string,
  ) => {
    try {
      await new FinanceService().assignCategory(txId, catId, reason);
      enqueueSnackbar("Category assigned!");
      setAssignOpen(false);
      setSelectedTx(null);
      fetchUnclassified();
      fetchStats();
      if (activeTab === "transactions") fetchTransactions(page, rowsPerPage);
    } catch {
      enqueueSnackbar("Failed to assign category", { variant: "error" });
    }
  };

  const handleConnectBankFeed = async () => {
    try {
      setConnectingFeed(true);
      const { redirectUrl } =
        await new FinanceService().initiateBankFeedOAuth();
      window.location.href = redirectUrl;
    } catch {
      enqueueSnackbar("Failed to initiate bank feed connection", {
        variant: "error",
      });
    } finally {
      setConnectingFeed(false);
    }
  };

  const handleGenerateVat100 = () => {
    // Download is handled inside ExportsReportsPanel's Vat100Card
  };

  const handleImport = async (file: File, format: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("format", format);
    await new FinanceService().importStatement(formData);
    enqueueSnackbar("Statement imported!");
    setImportOpen(false);
    setPeriodsRefreshKey((k) => k + 1);
    fetchStats();
    fetchUnclassified();
    if (activeTab === "transactions") fetchTransactions(0, rowsPerPage);
  };

  const handleResetFilter = () => {
    setFilterName("");
    setFilterCategory("all");
    setFilterDirection("all");
    setFilterStatus("all");
    setFilterMonth(null);
  };

  const handleBackToPeriods = () => {
    setSelectedPeriod(null);
    setFinanceStats(null);
    setTransactions([]);
    setUnclassified([]);
    setActiveTab("dashboard");
  };

  // ── Render: transaction table ─────────────────────────────────────────────
  const renderTable = (
    rows: IBankTransaction[],
    loading: boolean,
    count: number,
    showPagination = false,
  ) => (
    <>
      <TableContainer sx={{ position: "relative", overflow: "unset" }}>
        <Scrollbar>
          <Table size="medium" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map((h) => (
                  <TableCell key={h.id} align={h.align}>
                    {h.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {rows.map((row) => (
                    <FinanceTableRow
                      key={row.id}
                      row={row}
                      categories={categories}
                      onAssignCategory={() => {
                        setSelectedTx(row);
                        setAssignOpen(true);
                      }}
                    />
                  ))}
                  <TableNoData isNotFound={!loading && rows.length === 0} />
                </>
              )}
            </TableBody>
          </Table>
        </Scrollbar>
      </TableContainer>

      {showPagination && (
        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      )}
    </>
  );

  // ── Render: filters row ───────────────────────────────────────────────────
  const renderFilters = () => (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ mb: 2, mt: 1, flexWrap: "wrap", gap: 1 }}>
      <TextField
        size="small"
        value={filterName}
        onChange={(e) => setFilterName(e.target.value)}
        placeholder="Search narrative, counterparty..."
        sx={{ minWidth: 280 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: "text.disabled" }} />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        select
        size="small"
        label="Category"
        value={filterCategory}
        onChange={(e) => setFilterCategory(e.target.value)}
        sx={{ minWidth: 160 }}>
        <MenuItem value="all">All Categories</MenuItem>
        {selectableTransactionCategories(categories).map((c) => (
          <MenuItem key={c.id} value={String(c.id)}>
            {c.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Direction"
        value={filterDirection}
        onChange={(e) => setFilterDirection(e.target.value)}
        sx={{ minWidth: 130 }}>
        {DIRECTION_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Status"
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        sx={{ minWidth: 130 }}>
        {STATUS_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
      {/* Hide month picker when a period is already selected */}
      {!selectedPeriod && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Month"
            views={["year", "month"]}
            value={filterMonth}
            onChange={(v) => setFilterMonth(v)}
            slotProps={{
              textField: { size: "small", sx: { minWidth: 140 } },
            }}
          />
        </LocalizationProvider>
      )}
      {isFiltered && (
        <Button
          color="error"
          size="small"
          onClick={handleResetFilter}
          startIcon={<Iconify icon="eva:close-fill" />}>
          Reset
        </Button>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="caption" color="text.secondary">
        {totalCount} transaction{totalCount !== 1 ? "s" : ""}
      </Typography>
    </Stack>
  );

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Finance & VAT | OptoPro</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <CustomBreadcrumbs
          heading="Finance & VAT"
          links={[
            { name: "Dashboard", href: PATH_DASHBOARD.root },
            { name: "Finance & VAT" },
          ]}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              {selectedPeriod && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<Iconify icon="eva:arrow-back-fill" />}
                  onClick={handleBackToPeriods}
                  size="small">
                  All periods
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Iconify icon="eva:upload-fill" />}
                onClick={() => setImportOpen(true)}>
                Import Statement
              </Button>
              <IconButton
                onClick={() => {
                  if (selectedPeriod) {
                    fetchStats();
                    fetchUnclassified();
                    if (activeTab === "transactions")
                      fetchTransactions(0, rowsPerPage);
                  } else {
                    setPeriodsRefreshKey((k) => k + 1);
                  }
                }}>
                <Iconify icon="eva:refresh-fill" />
              </IconButton>
            </Stack>
          }
        />

        {/* ── PERIOD SELECTOR — shown when no period is selected ─────────── */}
        {!selectedPeriod && (
          <FinancePeriodSelector
            onSelectPeriod={setSelectedPeriod}
            refreshKey={periodsRefreshKey}
          />
        )}

        {/* ── DASHBOARD — shown when a period is selected ────────────────── */}
        {selectedPeriod && (
          <Card>
            {/* Period badge row */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ px: 2.5, pt: 2, pb: 0 }}>
              <Chip
                icon={
                  <Iconify
                    icon="eva:calendar-fill"
                    width={14}
                    sx={{ ml: 0.5 }}
                  />
                }
                label={selectedPeriod.label}
                color={PERIOD_COLOR[selectedPeriod.periodType]}
                variant="soft"
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                {selectedPeriod.dateFrom} → {selectedPeriod.dateTo}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                color="inherit"
                startIcon={<Iconify icon="eva:arrow-back-fill" width={14} />}
                onClick={handleBackToPeriods}
                sx={{ fontSize: 12 }}>
                Change period
              </Button>
            </Stack>

            {/* ── Tabs ── */}
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ px: 2, bgcolor: "background.neutral", mt: 1 }}
              variant="scrollable"
              scrollButtons="auto">
              {FINANCE_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Iconify icon={tab.icon} width={15} />
                      <span>{tab.label}</span>
                      {tab.value === "unclassified" &&
                        unclassifiedCount > 0 && (
                          <Chip
                            label={unclassifiedCount}
                            size="small"
                            color="warning"
                            sx={{ height: 18, fontSize: 10, ml: 0.5 }}
                          />
                        )}
                    </Stack>
                  }
                  sx={{
                    textTransform: "capitalize",
                    minHeight: 48,
                    fontSize: 13,
                  }}
                />
              ))}
            </Tabs>

            <Divider />

            <Box sx={{ p: 2.5 }}>
              {/* ── DASHBOARD TAB ── */}
              {activeTab === "dashboard" && (
                <FinanceDashboard
                  stats={financeStats}
                  vatSummary={vatSummary}
                  unclassifiedCount={unclassifiedCount}
                  categories={categories}
                  onGoToUnclassified={() => setActiveTab("unclassified")}
                  onGoToReconciliation={() => setActiveTab("reconciliation")}
                />
              )}

              {/* ── TRANSACTIONS TAB ── */}
              {activeTab === "transactions" && (
                <>
                  {unclassifiedCount > 0 && (
                    <Alert
                      severity="warning"
                      sx={{ mb: 2, borderRadius: 1 }}
                      action={
                        <Button
                          color="warning"
                          size="small"
                          onClick={() => setActiveTab("unclassified")}>
                          Review now
                        </Button>
                      }>
                      <AlertTitle>
                        {unclassifiedCount} transaction
                        {unclassifiedCount !== 1 ? "s" : ""} unclassified
                      </AlertTitle>
                      Assign a category to keep your accounts accurate.
                    </Alert>
                  )}
                  {renderFilters()}
                  {renderTable(transactions, loadingTx, totalCount, true)}
                </>
              )}

              {/* ── UNCLASSIFIED TAB ── */}
              {activeTab === "unclassified" && (
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle1" fontWeight={500}>
                        Unclassified Transactions
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        These transactions have no category. Assign one manually
                        or add a rule to auto-classify future ones.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Iconify icon="eva:settings-2-fill" />}
                        onClick={() => setActiveTab("rules")}>
                        Add Rule
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Iconify icon="eva:refresh-fill" />}
                        onClick={fetchUnclassified}
                        disabled={loadingUnc}>
                        Refresh
                      </Button>
                    </Stack>
                  </Stack>

                  {unclassifiedCount === 0 && !loadingUnc ? (
                    <Card sx={{ py: 6, textAlign: "center" }}>
                      <Iconify
                        icon="eva:checkmark-circle-2-fill"
                        width={44}
                        sx={{
                          color: "success.main",
                          display: "block",
                          mx: "auto",
                          mb: 1,
                        }}
                      />
                      <Typography variant="subtitle1" color="success.dark">
                        All transactions classified!
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Every transaction has a category assigned.
                      </Typography>
                    </Card>
                  ) : (
                    <>
                      <Alert severity="info" sx={{ borderRadius: 1 }}>
                        <Typography variant="caption">
                          Click the <strong>pencil icon</strong> on any row to
                          assign a category. Or click <strong>Add Rule</strong>{" "}
                          to auto-classify similar transactions in future
                          imports.
                        </Typography>
                      </Alert>
                      {renderTable(unclassified, loadingUnc, unclassifiedCount)}
                    </>
                  )}
                </Stack>
              )}

              {/* ── RECONCILIATION TAB ── */}
              {activeTab === "reconciliation" && (
                <ReconciliationPanel
                  transactions={
                    transactions.length > 0 ? transactions : unclassified
                  }
                  categories={categories}
                  onRefresh={() => {
                    fetchStats();
                    fetchUnclassified();
                    fetchTransactions(0, rowsPerPage);
                  }}
                />
              )}

              {/* ── VAT TAB ── */}
              {activeTab === "vat" && (
                <VatSummaryPanel selectedPeriod={selectedPeriod} />
              )}

              {/* ── EXPENSES TAB ── */}
              {activeTab === "expenses" && (
                <ExpensesPanel selectedPeriod={selectedPeriod} />
              )}

              {/* ── RULES TAB ── */}
              {activeTab === "rules" && (
                <RulesEnginePanel
                  categories={categories}
                  onAddRule={() => setAddRuleOpen(true)}
                  onRefresh={() => fetchTransactions(0, rowsPerPage)}
                  refreshKey={rulesRefreshKey}
                />
              )}

              {/* ── EXPORTS TAB ── */}
              {activeTab === "exports" && (
                <ExportsReportsPanel onGenerateVat100={handleGenerateVat100} />
              )}
            </Box>
          </Card>
        )}
      </Container>

      {/* ── Dialogs ── */}
      <AssignCategoryDialog
        open={assignOpen}
        transaction={selectedTx}
        categories={categories}
        onClose={() => {
          setAssignOpen(false);
          setSelectedTx(null);
        }}
        onSave={handleAssignSave}
      />

      <ImportStatementDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
      />

      <AddRuleDialog
        open={addRuleOpen}
        categories={categories}
        onClose={() => setAddRuleOpen(false)}
        onSave={async (rule) => {
          try {
            await new FinanceService().createRule(rule);
            enqueueSnackbar("Rule created!");
            setAddRuleOpen(false);
            setRulesRefreshKey((k) => k + 1);
          } catch {
            enqueueSnackbar("Failed to create rule", { variant: "error" });
          }
        }}
      />
    </>
  );
}
